import type { LoaderFunctionArgs } from 'react-router-dom';
import type { AssistantModel, Conversation, Message } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { loadWorkspaceContext } from '@/lib/auth/workspace-context';

// The chat surface supports several conversations per member. The active one is
// carried in the `?c=<id>` query param so a switch or a new chat is deep-linkable;
// absent → the most recent, or a freshly created one when the member has none.
export type ChatLoaderData = {
  readonly workspaceId: string;
  // `null` when the conversation could not be resolved — rendered as an inline
  // error rather than a blank composer.
  readonly conversation: Conversation | null;
  // The member's conversations, most-recent first, for the topbar switcher.
  readonly conversations: ReadonlyArray<Conversation>;
  readonly messages: ReadonlyArray<Message>;
  // Selectable models for the composer (IMM-351.1). Best-effort: empty when
  // discovery fails, in which case the composer sends no model and the backend
  // applies its default.
  readonly models: ReadonlyArray<AssistantModel>;
};

const CONVERSATION_LIST_LIMIT = 50;

// `?c=new` starts a fresh conversation: the loader returns no active conversation
// (a blank composer), and the page creates it lazily on the first message.
export const NEW_CHAT_SEARCH_VALUE = 'new';

async function loadModels(workspaceId: string): Promise<ReadonlyArray<AssistantModel>> {
  try {
    const result = await apiClient.conversations.listModels({ pathParams: { workspaceId } });

    return result.ok ? result.body.models : [];
  } catch {
    return [];
  }
}

async function listConversations(workspaceId: string): Promise<Conversation[]> {
  const result = await apiClient.conversations.list({
    pathParams: { workspaceId },
    query: { limit: CONVERSATION_LIST_LIMIT },
  });

  return result.ok ? [...result.body.conversations] : [];
}

// Resolve the active conversation plus the switcher list. A `?c=` id is honoured
// when it is one of the member's; otherwise (or absent) the most recent is used,
// creating one when the member has none yet.
async function resolveConversations(
  workspaceId: string,
  requestedId: string | null,
): Promise<{ active: Conversation | null; conversations: Conversation[] }> {
  const conversations = await listConversations(workspaceId);

  if (requestedId !== null) {
    const found = conversations.find((conversation) => conversation.id === requestedId);

    if (found !== undefined) return { active: found, conversations };

    // Not on the first page (or an invalid id): verify by id. 404 → fall back.
    const got = await apiClient.conversations.get({ pathParams: { workspaceId, conversationId: requestedId } });

    if (got.ok) return { active: got.body, conversations: [got.body, ...conversations] };
  }

  if (conversations.length > 0) return { active: conversations[0] ?? null, conversations };

  const created = await apiClient.conversations.create({ pathParams: { workspaceId }, body: {} });
  const active = created.ok ? created.body : null;

  return { active, conversations: active !== null ? [active] : [] };
}

async function loadHistory(workspaceId: string, conversationId: string): Promise<ReadonlyArray<Message>> {
  const result = await apiClient.conversations.listMessages({
    pathParams: { workspaceId, conversationId },
    query: { limit: 100 },
  });

  return result.ok ? result.body.messages : [];
}

export async function chatLoader({ params, request }: LoaderFunctionArgs): Promise<ChatLoaderData> {
  const ctx = await loadWorkspaceContext(params.workspaceId);
  const requestedId = new URL(request.url).searchParams.get('c');

  try {
    // A fresh chat: no active conversation yet, just the switcher list + models.
    if (requestedId === NEW_CHAT_SEARCH_VALUE) {
      const [conversations, models] = await Promise.all([
        listConversations(ctx.workspaceId),
        loadModels(ctx.workspaceId),
      ]);

      return { workspaceId: ctx.workspaceId, conversation: null, conversations, messages: [], models };
    }

    const { active, conversations } = await resolveConversations(ctx.workspaceId, requestedId);
    const [messages, models] = await Promise.all([
      active === null ? Promise.resolve([]) : loadHistory(ctx.workspaceId, active.id),
      loadModels(ctx.workspaceId),
    ]);

    return { workspaceId: ctx.workspaceId, conversation: active, conversations, messages, models };
  } catch {
    return { workspaceId: ctx.workspaceId, conversation: null, conversations: [], messages: [], models: [] };
  }
}
